import { z } from "zod";

import { SiteControllerType } from "@/lib/gql/__generated__/graphql";
import { SiteControllerTypeSchema } from "@/lib/zod/__generated__/validation-schema";

import { siteControllerSettingAirHostInputZodSchema } from "../../air-host/validation-schema";
import { siteControllerSettingBeds24InputZodSchema } from "../../beds24/validation-schema";
import { siteControllerSettingNeppanInputZodSchema } from "../../neppan/validation-schema";
import { siteControllerSettingNotUsingInputZodSchema } from "../../not-using/validation-schema";
import { siteControllerSettingTemairazuInputZodSchema } from "../../temairazu/validation-schema";
import { siteControllerSettingTllInputZodSchema } from "../../tl-lincoln/validation-schema";

/**
 * viewModelは全てのプロパティを持つが、validateのみ必要な箇所のみにかけたいので２種類定義している（関係ない設定箇所のバリデーションエラーで hasError: true になるから）
 *
 * - siteControllerSettingBaseInputDiscriminatedUnionZodSchema（type SiteControllerSettingBaseInputDiscriminatedUnionZodSchemaType）
 *   - siteControllerType によって判別し必要な箇所のみにバリデートするためのスキーマ
 *
 * - siteControllerSettingBaseInputZodSchema（type SiteControllerSettingBaseInputZodSchemaType）
 *   - viewModelで全ての値を保持していることを型として表現するためのスキーマ
 */
export const siteControllerSettingBaseInputDiscriminatedUnionZodSchema = z.discriminatedUnion("siteControllerType", [
	siteControllerSettingNotUsingInputZodSchema.extend({
		siteControllerType: z.literal(SiteControllerType.NotUsing),
	}),
	siteControllerSettingBeds24InputZodSchema.extend({ siteControllerType: z.literal(SiteControllerType.Beds24) }),
	siteControllerSettingNeppanInputZodSchema.extend({ siteControllerType: z.literal(SiteControllerType.Neppan) }),
	siteControllerSettingTemairazuInputZodSchema.extend({
		siteControllerType: z.literal(SiteControllerType.Temairazu),
	}),
	siteControllerSettingTllInputZodSchema.extend({ siteControllerType: z.literal(SiteControllerType.TlLincoln) }),
	siteControllerSettingAirHostInputZodSchema.extend({ siteControllerType: z.literal(SiteControllerType.AirHost) }),
]);

/**
 * viewModelは全てのプロパティを持つが、validateのみ必要な箇所のみにかけたいので２種類定義している（関係ない設定箇所のバリデーションエラーで hasError: true になるから）
 *
 * - siteControllerSettingBaseInputDiscriminatedUnionZodSchema（type SiteControllerSettingBaseInputDiscriminatedUnionZodSchemaType）
 *   - siteControllerType によって判別し必要な箇所のみにバリデートするためのスキーマ
 *
 * - siteControllerSettingBaseInputZodSchema（type SiteControllerSettingBaseInputZodSchemaType）
 *   - viewModelで全ての値を保持していることを型として表現するためのスキーマ
 */
export type SiteControllerSettingBaseInputDiscriminatedUnionZodSchemaType = z.infer<
	typeof siteControllerSettingBaseInputDiscriminatedUnionZodSchema
>;

/**
 * viewModelは全てのプロパティを持つが、validateのみ必要な箇所のみにかけたいので２種類定義している（関係ない設定箇所のバリデーションエラーで hasError: true になるから）
 *
 * - siteControllerSettingBaseInputDiscriminatedUnionZodSchema（type SiteControllerSettingBaseInputDiscriminatedUnionZodSchemaType）
 *   - siteControllerType によって判別し必要な箇所のみにバリデートするためのスキーマ
 *
 * - siteControllerSettingBaseInputZodSchema（type SiteControllerSettingBaseInputZodSchemaType）
 *   - viewModelで全ての値を保持していることを型として表現するためのスキーマ
 */
export const siteControllerSettingBaseInputZodSchema = z
	.object({
		// 型としては未選択が存在しない & 「CHILLNNでの料金管理」がデフォルト設定だが、
		// ユーザ自身で使用するサイコン設定を選択してほしいので拡張している
		siteControllerType: SiteControllerTypeSchema.or(z.literal("未選択")),

		setUpRoomPriceByChillnn: z.boolean(),
	})
	.merge(siteControllerSettingNotUsingInputZodSchema)
	.merge(siteControllerSettingBeds24InputZodSchema)
	.merge(siteControllerSettingNeppanInputZodSchema)
	.merge(siteControllerSettingTemairazuInputZodSchema)
	.merge(siteControllerSettingTllInputZodSchema)
	.merge(siteControllerSettingAirHostInputZodSchema);

/**
 * viewModelは全てのプロパティを持つが、validateのみ必要な箇所のみにかけたいので２種類定義している（関係ない設定箇所のバリデーションエラーで hasError: true になるから）
 *
 * - siteControllerSettingBaseInputDiscriminatedUnionZodSchema（type SiteControllerSettingBaseInputDiscriminatedUnionZodSchemaType）
 *   - siteControllerType によって判別し必要な箇所のみにバリデートするためのスキーマ
 *
 * - siteControllerSettingBaseInputZodSchema（type SiteControllerSettingBaseInputZodSchemaType）
 *   - viewModelで全ての値を保持していることを型として表現するためのスキーマ
 */
export type SiteControllerSettingBaseInputZodSchemaType = z.infer<typeof siteControllerSettingBaseInputZodSchema>;
